// ===================================================================
// MarketForge — Trade Verdict Card (extracted Iter118).
//
// Iter116 — consolidated pre-trade checkpoint. Replaces the right-rail
// trio of (WES-GEX Path + Strike Decision + Decision V2) with a single
// unambiguous card. Composes:
//   • candidates.decision  — from the cockpit-level /api/forge/candidates
//   • gexReport            — lifted from <GEXPanel /> onUpdate
//   • strike-decision data — fetched internally from /api/forge/strike-decision
//
// Layout:
//   A. Status pill  (ACCEPT / CAUTION / REJECT / NO TRADE)
//   B. Candidate Strike (one row: strike · premium · ROI vs gate)
//   C. GEX Risk Readout (regime · flip · pin · assignment · path)
//   D. Suggested Strikes (only when no candidate or status ≠ ACCEPT)
//   E. DO / DON'T playbook (side-by-side)
//   F. Plain-English narrative (one source of truth)
//   G. Pin button (only when trade_allowed)
//   H. "Show detailed breakdown" toggle — opens the legacy detail panels
//
// All data fetches are read-only; never writes to the journal.
// Anti-flicker via sdDataSymbolRef — only wipes verdict pane when the
// SYMBOL changes. Strike / engine / freshness changes paint over the
// previous verdict so the card never blanks mid-update.
// ===================================================================

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Layers, Loader2, AlertTriangle, ShieldAlert, CheckCircle2, Pin, Eye, EyeOff, ChevronDown, Copy, Check } from "lucide-react";
import InlineError from "@/components/InlineError";
import { GEX_CLASS_COLORS } from "./GEXPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtMoney(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

const VERDICT_STATUS_STYLES = {
  ACCEPT:             { bg: "bg-[#00FF88]/10", border: "border-[#00FF88]/40", text: "text-[#00FF88]", IconCmp: CheckCircle2 },
  CAUTION:            { bg: "bg-[#FFB800]/10", border: "border-[#FFB800]/40", text: "text-[#FFB800]", IconCmp: AlertTriangle },
  CAUTION_DATA_STALE: { bg: "bg-[#FFB800]/10", border: "border-[#FFB800]/40", text: "text-[#FFB800]", IconCmp: AlertTriangle },
  REJECT:             { bg: "bg-[#FF3366]/10", border: "border-[#FF3366]/40", text: "text-[#FF3366]", IconCmp: ShieldAlert },
  BLOCKED:            { bg: "bg-[#FF3366]/10", border: "border-[#FF3366]/40", text: "text-[#FF3366]", IconCmp: ShieldAlert },
  NO_TRADE_ROI_FAIL:  { bg: "bg-[#FF3366]/10", border: "border-[#FF3366]/40", text: "text-[#FF3366]", IconCmp: ShieldAlert },
  NO_TRADE:           { bg: "bg-[#FF3366]/10", border: "border-[#FF3366]/40", text: "text-[#FF3366]", IconCmp: ShieldAlert },
};

const RISK_COLOR = (v) => {
  if (v === "HIGH" || v === "EXTREME") return "text-[#FF3366]";
  if (v === "MODERATE") return "text-[#FFB800]";
  if (v === "LOW" || v === "MINIMAL") return "text-[#00FF88]";
  return "text-[#8A8A93]";
};

export function TradeVerdictCard({ symbol, wesEngine, candidates, candidateStrike, strategy,
                                    freshnessState, gexReport, onPin, pinResult, onEvaluate,
                                    detailsOpen, onToggleDetails, t }) {
  const [sdData, setSdData] = useState(null);
  const [sdLoading, setSdLoading] = useState(false);
  const [sdErr, setSdErr] = useState(null);
  // Iter116.1 — "Copy verdict" affordance. Renders a one-liner to the
  // clipboard so the operator can paste the verdict into Discord, a
  // notebook, or a chat without taking a screenshot. Pure UX sugar — no
  // backend write. `copied` is a 2-second ephemeral confirmation state.
  const [copied, setCopied] = useState(false);
  // Iter117.8 — Anti-flicker: only nuke the verdict pane when the
  // SYMBOL changes. Strike/engine/freshness changes paint over the
  // previous verdict so the card never blanks out mid-update.
  const sdDataSymbolRef = useRef(null);

  // Mirror StrikeDecisionCard's fetch contract — same params, same symbol
  // hardening so the cockpit always shows the right symbol's verdict.
  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    const symbolChanged = symbol !== sdDataSymbolRef.current;
    if (symbolChanged) {
      setSdData(null);
      setSdErr(null);
    }
    setSdLoading(true);
    const params = { symbol, strategy,
                      engine: wesEngine === "legacy" ? "legacy" : "parity",
                      freshness_state: freshnessState || "FRESH" };
    if (candidateStrike) params.candidate_strike = candidateStrike;
    axios.get(`${API}/forge/strike-decision`, { params })
      .then(r => {
        if (!alive) return;
        if (r.data?.symbol && r.data.symbol.toUpperCase() !== symbol.toUpperCase()) {
          setSdErr(`TRADE VERDICT DATA MISMATCH — ${r.data.symbol} returned for ${symbol}`);
          return;
        }
        setSdData(r.data);
        setSdErr(null);
        sdDataSymbolRef.current = symbol;
      })
      .catch(e => { if (alive) setSdErr(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setSdLoading(false); });
    return () => { alive = false; };
  }, [symbol, wesEngine, candidateStrike, strategy, freshnessState]);

  const dec = candidates?.decision || null;
  // Resolve the final status. Strike-decision is the authoritative source
  // when it lands; fall back to candidates.decision while loading.
  const status = sdData?.status
    || (dec ? (dec.trade_allowed ? "ACCEPT" : "NO_TRADE") : null);
  const styles = status ? (VERDICT_STATUS_STYLES[status] || VERDICT_STATUS_STYLES.CAUTION) : null;
  const StatusIcon = styles?.IconCmp;
  const strategyLabel = dec?.strategy === "PURE_WHEEL_ITM" ? t("forge.label_pure")
    : dec?.strategy === "WHEEL_LADDER_ITM" ? t("forge.label_ladder")
    : dec?.strategy === "NO_TRADE" ? t("forge.label_no_trade")
    : (dec?.strategy || "—");

  const cand = sdData?.candidate_strike_analysis || null;
  const gexClassLabel = gexReport?.classification && gexReport.classification !== "NO_DATA"
    ? t(`gex.class.${gexReport.classification}`) : null;
  const dealerRegime = sdData?.dealer_regime || cand?.dealer_regime || null;

  // Iter116.1 — Build the one-liner. Skips empty fields so the output
  // stays compact when data is partial (e.g. no candidate strike yet).
  const buildCopyLine = () => {
    const parts = [];
    parts.push(symbol || "—");
    parts.push(status || "—");
    if (sdData?.candidate_strike != null) parts.push(`@ $${Number(sdData.candidate_strike).toFixed(2)}`);
    if (sdData?.roi_gate?.actual_bid != null) {
      const roi = (sdData.roi_gate.actual_bid * 100).toFixed(2);
      const gate = candidates?.min_income_pct;
      parts.push(`ROI ${roi}%${gate != null ? ` ${sdData.roi_gate.passed ? "≥" : "<"} ${gate}%` : ""}`);
    }
    if (gexClassLabel) parts.push(`GEX ${gexReport.classification.replace(/_/g, " ")}`);
    if (candidates?.expiration) parts.push(`exp ${candidates.expiration} (${candidates.dte}d)`);
    return parts.join(" · ");
  };
  const handleCopyVerdict = async () => {
    const line = buildCopyLine();
    try {
      await navigator.clipboard.writeText(line);
    } catch {
      // Fallback for older browsers — use a temp textarea.
      const ta = document.createElement("textarea");
      ta.value = line;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* swallow */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  const canCopy = !!status;

  return (
    <div data-testid="forge-trade-verdict-card"
         className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-[#00FF88]" />
          <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">
            {t("forge.trade_verdict")}
          </h3>
          {sdLoading && <Loader2 size={12} className="animate-spin text-[#8A8A93]" />}
        </div>
        <div className="flex items-center gap-1.5">
          {/* Iter116.1 — Copy verdict to clipboard */}
          <button
            type="button"
            data-testid="forge-trade-verdict-copy"
            onClick={handleCopyVerdict}
            disabled={!canCopy}
            title={t("forge.copy_verdict")}
            className={`flex items-center gap-1 text-[8px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border transition-colors ${
              copied
                ? "border-[#00FF88]/60 text-[#00FF88] bg-[#00FF88]/10"
                : "border-[#26262B] text-[#8A8A93] hover:text-white hover:border-[#3F3F47] disabled:opacity-30 disabled:cursor-not-allowed"
            }`}
          >
            {copied ? <Check size={10} /> : <Copy size={10} />}
            <span data-testid="forge-trade-verdict-copy-label">
              {copied ? t("forge.copied") : t("forge.copy")}
            </span>
          </button>
          <span className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66]">
            PARITY v1 · GEX · ROI
          </span>
        </div>
      </div>

      {sdErr && (
        <InlineError
          testId="forge-trade-verdict-error"
          className="mb-3"
          message={sdErr}
        />
      )}

      {/* A. STATUS PILL — single source of truth */}
      {styles && (
        <div data-testid="forge-trade-verdict-status"
             className={`${styles.bg} border ${styles.border} rounded-sm px-3 py-2 mb-3`}>
          <div className="flex items-center gap-2 flex-wrap">
            {StatusIcon && <StatusIcon size={14} className={styles.text} />}
            <div className={`text-[13px] font-black uppercase tracking-[0.12em] ${styles.text}`}>
              {status} · {strategyLabel}
            </div>
            {gexClassLabel && (
              <span
                data-testid="forge-trade-verdict-gex-chip"
                title={t("gex.classification") + ": " + gexClassLabel + " · " + t("gex.score") + " " + gexReport.score}
                className="text-[8px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm"
                style={{ color: GEX_CLASS_COLORS[gexReport.classification], border: `1px solid ${GEX_CLASS_COLORS[gexReport.classification]}55`, background: `${GEX_CLASS_COLORS[gexReport.classification]}10` }}
              >
                GEX · {gexClassLabel}
              </span>
            )}
          </div>
          {sdData?.action_label && (
            <div className={`mt-1 text-[10px] font-mono ${styles.text} opacity-90`}>
              {sdData.action_label}
            </div>
          )}
          {dec?.reason && (
            <p className="mt-1 text-[10px] font-mono text-[#E4E4E7] opacity-90 leading-relaxed">
              {/* Iter116 — Strip the "Bias: ..." suffix the backend appends.
                  The bias narrative already lives in the WES Structure card
                  above; echoing it here was the #1 source of duplicate-text
                  noise the operator flagged. */}
              {dec.reason.replace(/\s*Bias:[\s\S]*$/i, "").trim()}
            </p>
          )}
          {sdData && (
            <div className="mt-1 flex items-center gap-2 text-[8px] font-mono text-[#8A8A93]">
              <span>SCORE {sdData.decision_score}</span>
              <span>·</span>
              <span>CONFIDENCE {sdData.confidence}</span>
            </div>
          )}
        </div>
      )}

      {/* B. CANDIDATE STRIKE — shown once */}
      {(candidates?.expiration || sdData?.candidate_strike != null) && (
        <div data-testid="forge-trade-verdict-candidate"
             className="bg-[#08080A] border border-[#26262B] rounded-sm px-2.5 py-2 mb-3">
          <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-1.5">
            {t("forge.candidate_strike")}{candidates?.expiration ? ` · ${candidates.expiration} (${candidates.dte}d)` : ""}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono">
            {sdData?.candidate_strike != null && (
              <>
                <span className="text-[#8A8A93]">{t("forge.primary_k")}</span>
                <span className="text-right text-white font-bold">{fmtMoney(sdData.candidate_strike)}</span>
              </>
            )}
            {dec?.secondary_strike != null && (
              <>
                <span className="text-[#8A8A93]">{t("forge.secondary_k")}</span>
                <span className="text-right text-white">{fmtMoney(dec.secondary_strike)}</span>
              </>
            )}
            {dec?.premium_bid != null && (
              <>
                <span className="text-[#8A8A93]">{t("forge.premium_bid")}</span>
                <span className="text-right text-[#00FF88]">{fmtMoney(dec.premium_bid)}</span>
              </>
            )}
            {sdData?.roi_gate?.actual_bid != null && (
              <>
                <span className="text-[#8A8A93]">{t("forge.roi_bid")} {t("forge.gate")}</span>
                <span className={`text-right font-bold ${sdData.roi_gate.passed ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
                  {(sdData.roi_gate.actual_bid * 100).toFixed(2)}% {sdData.roi_gate.passed ? "✓" : "✕"}
                  {candidates?.min_income_pct ? ` (≥${candidates.min_income_pct}%)` : ""}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* C. GEX RISK READOUT — pin/assignment/path/regime/flip in one place */}
      {(cand || dealerRegime || sdData?.flip_level != null) && (
        <div data-testid="forge-trade-verdict-gex-risk"
             className="bg-[#08080A] border border-[#26262B] rounded-sm px-2.5 py-2 mb-3">
          <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-1.5">
            {t("forge.gex_risk_readout")}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono">
            {dealerRegime && (
              <>
                <span className="text-[#8A8A93]">{t("forge.dealer_regime")}</span>
                <span className={`text-right font-bold ${
                  dealerRegime === "POSITIVE_GEX_PINNING_BIAS" ? "text-[#00FF88]" :
                  dealerRegime === "NEGATIVE_GEX_TREND_RISK" ? "text-[#FF3366]" :
                  dealerRegime === "MIXED_GEX" ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>
                  {dealerRegime.replace(/_/g, " ")}
                </span>
              </>
            )}
            {sdData?.flip_level != null && (
              <>
                <span className="text-[#8A8A93]">{t("forge.flip_spot")}</span>
                <span className={`text-right font-bold ${
                  sdData.spot_vs_flip === "ABOVE_FLIP" ? "text-[#00FF88]" :
                  sdData.spot_vs_flip === "BELOW_FLIP" ? "text-[#FF3366]" : "text-[#FFB800]"}`}>
                  {fmtMoney(sdData.flip_level)} · {(sdData.spot_vs_flip || "").replace(/_/g, " ")}
                  {sdData.distance_to_flip_pct != null ? ` (${sdData.distance_to_flip_pct > 0 ? "+" : ""}${sdData.distance_to_flip_pct}%)` : ""}
                </span>
              </>
            )}
            {cand?.pin_risk && (
              <>
                <span className="text-[#8A8A93]">{t("forge.pin_risk")}</span>
                <span className={`text-right font-bold ${RISK_COLOR(cand.pin_risk)}`}>{cand.pin_risk}</span>
              </>
            )}
            {cand?.assignment_magnet_risk && (
              <>
                <span className="text-[#8A8A93]">{t("forge.assignment_risk")}</span>
                <span className={`text-right font-bold ${RISK_COLOR(cand.assignment_magnet_risk)}`}>{cand.assignment_magnet_risk}</span>
              </>
            )}
            {cand?.path_quality && (
              <>
                <span className="text-[#8A8A93]">{t("forge.path_quality")}</span>
                <span className={`text-right font-bold ${
                  ["BLOCKED","FRAGILE","THIN_AIR","BREAKOUT_RISK"].includes(cand.path_quality) ? "text-[#FF3366]" :
                  cand.path_quality === "SUPPORTED_BUT_PINNED" ? "text-[#FFB800]" :
                  cand.path_quality === "SUPPORTED" ? "text-[#00FF88]" : "text-[#8A8A93]"}`}>
                  {cand.path_quality.replace(/_/g, " ")}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* D. SUGGESTED STRIKES — only when no candidate is locked in */}
      {sdData?.candidate_strike == null && (sdData?.suggested_candidates?.length || 0) > 0 && (
        <div data-testid="forge-trade-verdict-suggestions"
             className="bg-[#08080A] border border-[#26262B] rounded-sm px-2.5 py-2 mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#FFB800]">
              {t("forge.suggested_strikes")}
            </div>
            <div className="text-[8px] font-mono text-[#5F5F66]">click Evaluate</div>
          </div>
          <div className="space-y-1.5">
            {sdData.suggested_candidates.map((s, i) => (
              <div key={`${s.strike}-${i}`}
                   data-testid={`forge-trade-verdict-suggestion-${i}`}
                   className={`rounded-sm border px-1.5 py-1.5 text-[9px] font-mono ${
                     s.below_roi_gate
                       ? "bg-[#FFB800]/5 border-[#FFB800]/30"
                       : "bg-[#0A0A0E] border-[#26262B] hover:border-[#00FF88]/40"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#8A8A93]">
                      {s.strategy?.replace("WHEEL_ITM_", "")}
                    </span>
                    <span className="text-white font-bold">{fmtMoney(s.strike)}</span>
                  </div>
                  <button
                    type="button"
                    data-testid={`forge-trade-verdict-suggestion-evaluate-${i}`}
                    onClick={() => onEvaluate && onEvaluate(Number(s.strike))}
                    className="text-[8px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm border bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40 hover:bg-[#00FF88]/25"
                  >Evaluate</button>
                </div>
                <div className="grid grid-cols-2 gap-x-2 mt-1 text-[9px]">
                  <span className="text-[#8A8A93]">ROI</span>
                  <span className={`text-right font-bold ${s.below_roi_gate ? "text-[#FFB800]" : "text-[#00FF88]"}`}>
                    {s.roi_pct?.toFixed(2)}%{s.below_roi_gate ? " · below gate" : " ✓"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* E. DO / DON'T playbook */}
      {(dec?.do_rules?.length > 0 || dec?.dont_rules?.length > 0) && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {dec.do_rules?.length > 0 && (
            <div>
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#00FF88] mb-1">{t("forge.do")}</div>
              <ul className="text-[10px] text-[#E4E4E7] space-y-0.5 list-disc pl-3.5 leading-snug">
                {dec.do_rules.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {dec.dont_rules?.length > 0 && (
            <div>
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#FF3366] mb-1">{t("forge.dont")}</div>
              <ul className="text-[10px] text-[#E4E4E7] space-y-0.5 list-disc pl-3.5 leading-snug">
                {dec.dont_rules.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* F. PLAIN ENGLISH — one shared narrative */}
      {dec?.execution_plan && (
        <div data-testid="forge-trade-verdict-plain"
             className="text-[10px] font-mono text-[#A0A0A8] italic leading-relaxed mb-3 pt-2 border-t border-[#26262B]">
          {dec.execution_plan}
        </div>
      )}

      {/* G. PIN BUTTON — only when the underlying decision permits it */}
      {dec?.trade_allowed && (
        <button
          data-testid="forge-trade-verdict-pin"
          onClick={onPin}
          className="w-full flex items-center justify-center gap-1.5 bg-[#00FF88] text-[#08080A] font-bold px-3 py-2 rounded-sm text-xs mb-2"
        >
          <Pin size={12} /> {t("forge.pin_button")}
        </button>
      )}
      {pinResult && (
        <div data-testid="forge-trade-verdict-pin-result"
             className={`mt-1 mb-2 text-[10px] font-mono ${pinResult.kind === "ok" ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
          {pinResult.kind === "ok" ? `✓ ${pinResult.msg}` : `✗ ${pinResult.msg}`}
        </div>
      )}

      {/* H. DRILL-DOWN TOGGLE — preserves access to the legacy detail panels */}
      <button
        type="button"
        data-testid="forge-trade-verdict-details-toggle"
        onClick={onToggleDetails}
        className="w-full mt-1 flex items-center justify-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] hover:text-[#E0E0E0] border border-[#26262B] hover:border-[#3F3F47] px-2 py-1.5 rounded-sm"
      >
        {detailsOpen ? <EyeOff size={11} /> : <Eye size={11} />}
        {detailsOpen ? t("forge.hide_detailed_breakdown") : t("forge.show_detailed_breakdown")}
        <ChevronDown size={11} className={detailsOpen ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
    </div>
  );
}

export default TradeVerdictCard;
